import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, TrendingUp, TrendingDown, Minus, ListChecks, CheckSquare, Square, ChevronDown, ChevronUp } from "lucide-react";

interface BudgetItem {
  id: string;
  wbs: string;
  name: string;
  plannedValue: number;
  actualCost: number;
  percentComplete: number;
}

const EMPTY_ITEM: Omit<BudgetItem, "id"> = { wbs: "", name: "", plannedValue: 0, actualCost: 0, percentComplete: 0 };

// Project budgeting working checklist (PM / BA). Derived from standard budgeting
// best practices: plan → categorize costs → baseline → reserve analysis → control.
const BUDGET_STEPS: { key: string; label: string; blurb: string; items: string[] }[] = [
  {
    key: "planning",
    label: "1 · Plan the budget",
    blurb: "Built in the initiation and planning phases.",
    items: [
      "Reference historical data from similar past projects — what went well, what to avoid",
      "Have the team, a mentor or your manager double-check the budget",
      "Time-phase the budget: allocate task costs across the projected timeline",
      "Verify every calculation is accurate and error-free",
      "Format it to be straightforward for finance / senior-management approval",
    ],
  },
  {
    key: "costs",
    label: "2 · Categorize costs",
    blurb: "Meet the organization's and customer's requirements.",
    items: [
      "Direct costs: wages/salaries of employees and contractors, materials, equipment rental, software licenses, project travel/transport, staff training",
      "Indirect / overhead costs: administrative, utilities, insurance, general office equipment, security",
      "Tag each line as direct or indirect so requirements are auditable",
    ],
  },
  {
    key: "baseline",
    label: "3 · Develop a baseline",
    blurb: "The starting estimate you measure against.",
    items: [
      "Get the budget approved",
      "Publish the baseline budget",
      "Compare actual performance against the baseline throughout the life cycle",
      "Route budget updates through the same approvals as the original",
      "Re-baseline after any approved change to schedule, cost or deliverable content (e.g. scope change)",
    ],
  },
  {
    key: "reserve",
    label: "4 · Reserve analysis",
    blurb: "Account for buffer funds.",
    items: [
      "Review all project risks; add a contingency budget where buffer funds are needed",
      "Account for cost of quality: rework, defects, internal and external failure costs, customer refunds",
    ],
  },
  {
    key: "control",
    label: "5 · Monitor & control",
    blurb: "Review and control across the life cycle.",
    items: [
      "Continually track planned vs. actual cost over time (time-phased)",
      "Use the budget as a stakeholder communication tool and progress tracker",
      "Make informed adjustments with the required approvals; re-baseline on significant change",
    ],
  },
];

function StatusBadge({ value, label }: { value: number; label: string }) {
  const good = label === "SPI" || label === "CPI" ? value >= 1 : true;
  const color = !isFinite(value) ? "bg-muted text-muted-foreground border-border"
    : value >= 1 ? "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/20 dark:text-green-400"
    : value >= 0.9 ? "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/20 dark:text-amber-400"
    : "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/20 dark:text-red-400";
  const Icon = !isFinite(value) ? Minus : value >= 1 ? TrendingUp : TrendingDown;
  return (
    <Badge variant="outline" className={`${color} gap-1 text-[11px]`}>
      <Icon className="h-3 w-3" /> {label}: {isFinite(value) ? value.toFixed(2) : "—"}
    </Badge>
  );
}

export default function BudgetEVMPanel({ projectId }: { projectId?: string }) {
  const [items, setItems] = useState<BudgetItem[]>([]);
  const [draft, setDraft] = useState(EMPTY_ITEM);

  const checkKey = `budget-checklist:${projectId || "default"}`;
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [openSteps, setOpenSteps] = useState<Record<string, boolean>>({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem(checkKey);
      setChecks(raw ? JSON.parse(raw) : {});
    } catch { setChecks({}); }
  }, [checkKey]);

  const toggleCheck = (id: string) => {
    setChecks(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem(checkKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const totalChecks = BUDGET_STEPS.reduce((n, s) => n + s.items.length, 0);
  const doneChecks = BUDGET_STEPS.reduce(
    (n, s) => n + s.items.filter((_, i) => checks[`${s.key}:${i}`]).length, 0);

  const addItem = () => {
    if (!draft.name.trim()) return;
    setItems(prev => [...prev, { ...draft, id: crypto.randomUUID() }]);
    setDraft(EMPTY_ITEM);
  };

  const updateItem = (id: string, field: keyof BudgetItem, value: any) => {
    setItems(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const deleteItem = (id: string) => setItems(prev => prev.filter(i => i.id !== id));

  const totals = useMemo(() => {
    const PV = items.reduce((s, i) => s + i.plannedValue, 0);
    const AC = items.reduce((s, i) => s + i.actualCost, 0);
    const EV = items.reduce((s, i) => s + i.plannedValue * (i.percentComplete / 100), 0);
    const SPI = PV > 0 ? EV / PV : 0;
    const CPI = AC > 0 ? EV / AC : 0;
    const CV = EV - AC;
    const SV = EV - PV;
    const EAC = CPI > 0 ? PV / CPI : 0;
    const ETC = EAC - AC;
    const VAC = PV - EAC;
    return { PV, AC, EV, SPI, CPI, CV, SV, EAC, ETC, VAC };
  }, [items]);

  const fmt = (n: number) => n === 0 ? "—" : `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 border-b border-border px-5 py-3">
        <h2 className="text-sm font-semibold">Budget & Earned Value Management</h2>
        <p className="text-[11px] text-muted-foreground">Track planned vs actual cost. EVM metrics calculated automatically.</p>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5" style={{ scrollbarWidth: "thin" }}>

        {/* ── Project budgeting checklist (PM / BA) ───────────────────────── */}
        <div className="border border-border rounded-lg bg-card overflow-hidden">
          <button
            onClick={() => setChecklistOpen(o => !o)}
            className="w-full px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-muted/20"
          >
            <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
              <ListChecks className="h-4 w-4" /> Project Budgeting Checklist
              <span className="text-[10px] font-mono text-muted-foreground">PM / BA</span>
            </span>
            <span className="flex items-center gap-2">
              <span className="text-[11px] font-mono text-muted-foreground">{doneChecks}/{totalChecks}</span>
              {checklistOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </span>
          </button>

          {checklistOpen && (
            <div className="border-t border-border p-3 space-y-2 bg-muted/10">
              <p className="text-[11px] text-muted-foreground">
                The budget is a cost control, a stakeholder communication tool and a progress tracker.
                Build it in initiation/planning, then review and control it across the whole life cycle.
                Progress is saved to this browser.
              </p>

              {BUDGET_STEPS.map(step => {
                const stepDone = step.items.filter((_, i) => checks[`${step.key}:${i}`]).length;
                const isOpen = openSteps[step.key] ?? true;
                return (
                  <div key={step.key} className="border border-border rounded bg-card">
                    <button
                      onClick={() => setOpenSteps(o => ({ ...o, [step.key]: !(o[step.key] ?? true) }))}
                      className="w-full p-2 flex items-center justify-between gap-2 hover:bg-muted/20"
                    >
                      <span className="text-left">
                        <span className="text-xs font-semibold text-foreground">{step.label}</span>
                        <span className="block text-[10px] text-muted-foreground">{step.blurb}</span>
                      </span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        <span className="text-[10px] font-mono text-muted-foreground">{stepDone}/{step.items.length}</span>
                        {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      </span>
                    </button>
                    {isOpen && (
                      <ul className="border-t border-border p-2 space-y-1">
                        {step.items.map((item, i) => {
                          const id = `${step.key}:${i}`;
                          const done = !!checks[id];
                          return (
                            <li key={id}>
                              <button
                                onClick={() => toggleCheck(id)}
                                className="w-full flex items-start gap-2 text-left text-[11px] py-0.5"
                              >
                                {done
                                  ? <CheckSquare className="h-3.5 w-3.5 shrink-0 mt-0.5 text-green-600" />
                                  : <Square className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground" />}
                                <span className={done ? "line-through text-muted-foreground" : "text-foreground"}>{item}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* EVM Summary cards */}
        {items.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Planned Value (PV)", value: fmt(totals.PV), sub: "Budget allocated to date" },
              { label: "Earned Value (EV)", value: fmt(totals.EV), sub: "Work completed (value)" },
              { label: "Actual Cost (AC)", value: fmt(totals.AC), sub: "Money spent to date" },
              { label: "EAC (Forecast)", value: fmt(totals.EAC), sub: "Estimated cost at completion" },
            ].map(card => (
              <div key={card.label} className="border border-border rounded-lg p-3 bg-card">
                <p className="text-[10px] font-mono uppercase text-muted-foreground">{card.label}</p>
                <p className="text-xl font-bold text-foreground mt-0.5">{card.value}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{card.sub}</p>
              </div>
            ))}
          </div>
        )}

        {/* Variance + indexes */}
        {items.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <StatusBadge value={totals.SPI} label="SPI" />
            <StatusBadge value={totals.CPI} label="CPI" />
            <Badge variant="outline" className={`text-[11px] gap-1 ${totals.CV >= 0 ? "bg-green-100 text-green-700 border-green-300" : "bg-red-100 text-red-700 border-red-300"}`}>
              CV: {totals.CV >= 0 ? "+" : ""}{fmt(totals.CV)}
            </Badge>
            <Badge variant="outline" className={`text-[11px] gap-1 ${totals.SV >= 0 ? "bg-green-100 text-green-700 border-green-300" : "bg-amber-100 text-amber-700 border-amber-300"}`}>
              SV: {totals.SV >= 0 ? "+" : ""}{fmt(totals.SV)}
            </Badge>
            <Badge variant="outline" className="text-[11px] bg-muted text-muted-foreground border-border">
              ETC: {fmt(totals.ETC)}
            </Badge>
            <Badge variant="outline" className={`text-[11px] ${totals.VAC >= 0 ? "bg-green-100 text-green-700 border-green-300" : "bg-red-100 text-red-700 border-red-300"}`}>
              VAC: {totals.VAC >= 0 ? "+" : ""}{fmt(totals.VAC)}
            </Badge>
          </div>
        )}

        {/* Legend */}
        {items.length > 0 && (
          <div className="text-[10px] text-muted-foreground font-mono space-x-3">
            <span>SPI = schedule performance (≥1 ahead)</span>
            <span>CPI = cost performance (≥1 under budget)</span>
            <span>EAC = PV/CPI</span>
          </div>
        )}

        {/* Table */}
        <div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">WBS</th>
                  <th className="text-left px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">Work Package</th>
                  <th className="text-right px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">PV ($)</th>
                  <th className="text-right px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">AC ($)</th>
                  <th className="text-right px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">% Done</th>
                  <th className="text-right px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">EV ($)</th>
                  <th className="text-right px-3 py-2 text-[10px] font-mono uppercase text-muted-foreground">CPI</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const ev = item.plannedValue * (item.percentComplete / 100);
                  const cpi = item.actualCost > 0 ? ev / item.actualCost : null;
                  return (
                    <tr key={item.id} className="border-b border-border/50 hover:bg-muted/20">
                      <td className="px-3 py-2">
                        <Input value={item.wbs} onChange={e => updateItem(item.id, "wbs", e.target.value)}
                          className="h-6 text-[11px] font-mono w-16 border-0 bg-transparent p-0 focus-visible:ring-0" placeholder="1.1" />
                      </td>
                      <td className="px-3 py-2">
                        <Input value={item.name} onChange={e => updateItem(item.id, "name", e.target.value)}
                          className="h-6 text-[12px] w-full border-0 bg-transparent p-0 focus-visible:ring-0" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Input type="number" value={item.plannedValue || ""} onChange={e => updateItem(item.id, "plannedValue", +e.target.value)}
                          className="h-6 text-[12px] w-24 border-0 bg-transparent p-0 focus-visible:ring-0 text-right" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Input type="number" value={item.actualCost || ""} onChange={e => updateItem(item.id, "actualCost", +e.target.value)}
                          className="h-6 text-[12px] w-24 border-0 bg-transparent p-0 focus-visible:ring-0 text-right" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Input type="number" min={0} max={100} value={item.percentComplete || ""} onChange={e => updateItem(item.id, "percentComplete", +e.target.value)}
                          className="h-6 text-[12px] w-16 border-0 bg-transparent p-0 focus-visible:ring-0 text-right" />
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-muted-foreground">${ev.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      <td className="px-3 py-2 text-right">
                        {cpi !== null ? (
                          <span className={`font-mono font-bold ${cpi >= 1 ? "text-green-600" : cpi >= 0.9 ? "text-amber-600" : "text-red-600"}`}>
                            {cpi.toFixed(2)}
                          </span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-2 py-2">
                        <button onClick={() => deleteItem(item.id)} className="text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {/* Add row */}
                <tr className="bg-muted/10">
                  <td className="px-3 py-2">
                    <Input value={draft.wbs} onChange={e => setDraft(d => ({ ...d, wbs: e.target.value }))}
                      className="h-6 text-[11px] font-mono w-16" placeholder="1.1" />
                  </td>
                  <td className="px-3 py-2">
                    <Input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                      className="h-6 text-[12px] w-full" placeholder="Work package name…" />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" value={draft.plannedValue || ""} onChange={e => setDraft(d => ({ ...d, plannedValue: +e.target.value }))}
                      className="h-6 text-[12px] w-24" placeholder="0" />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" value={draft.actualCost || ""} onChange={e => setDraft(d => ({ ...d, actualCost: +e.target.value }))}
                      className="h-6 text-[12px] w-24" placeholder="0" />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" min={0} max={100} value={draft.percentComplete || ""} onChange={e => setDraft(d => ({ ...d, percentComplete: +e.target.value }))}
                      className="h-6 text-[12px] w-16" placeholder="0" />
                  </td>
                  <td className="px-3 py-2" colSpan={2}></td>
                  <td className="px-2 py-2">
                    <Button size="sm" variant="outline" className="h-6 px-2" onClick={addItem}><Plus className="h-3 w-3" /></Button>
                  </td>
                </tr>
              </tbody>
              {items.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-border bg-muted/30 font-semibold">
                    <td className="px-3 py-2 text-[11px] font-mono text-muted-foreground" colSpan={2}>TOTAL</td>
                    <td className="px-3 py-2 text-right text-[12px]">{fmt(totals.PV)}</td>
                    <td className="px-3 py-2 text-right text-[12px]">{fmt(totals.AC)}</td>
                    <td className="px-3 py-2"></td>
                    <td className="px-3 py-2 text-right text-[12px]">{fmt(totals.EV)}</td>
                    <td className="px-3 py-2 text-right text-[12px]">
                      <span className={totals.CPI >= 1 ? "text-green-600" : "text-red-600"}>{totals.CPI.toFixed(2)}</span>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
